<script setup>
import { router } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import FeedbackDrawer from './FeedbackDrawer.vue'

/**
 * RED-389: Aruande kontroll → Tagasiside (design-spec §6, second row). Every
 * appraiser's `Õige` / `Vale` from RED-388, across all companies, 20 per page,
 * newest first. Filters and paging are query parameters, so a link can open
 * a filtered list; changing one partial-reloads only the `feedback` prop.
 */
const props = defineProps({
  feedback: { type: Object, required: true },
})

const { t } = useI18n()

const filters = computed(() => props.feedback.filters)

const periodOptions = computed(() => [
  { value: '7', label: t('messages.admin.report_qc.feedback.period7') },
  { value: '30', label: t('messages.admin.report_qc.feedback.period30') },
  { value: 'all', label: t('messages.admin.report_qc.feedback.periodAll') },
])

const questionOptions = computed(() => [
  { value: null, label: t('messages.admin.report_qc.feedback.allQuestions') },
  ...props.feedback.questions.map(question => ({
    value: question.key,
    label: question.title ? `${question.key} · ${question.title}` : question.key,
  })),
])

const companyOptions = computed(() => [
  { value: null, label: t('messages.admin.report_qc.feedback.companyAll') },
  ...props.feedback.companies.map(company => ({ value: company.id, label: company.name })),
])

const summaryLine = computed(() => {
  const summary = props.feedback.summary
  if (!summary.top || summary.vale === 0) {
    return t('messages.admin.report_qc.feedback.summaryNoVale', { n: summary.total }, summary.total)
  }
  return t('messages.admin.report_qc.feedback.summary', {
    n: summary.total,
    vale: summary.vale,
    key: summary.top.key,
    title: summary.top.title,
    k: summary.top.count,
  }, summary.total)
})

const loading = ref(false)

/** One place builds the query string, so a filter change resets the page. */
const visit = (changes, page = 1) => {
  const next = { ...filters.value, ...changes }
  router.get(
    route('admin.report-qc.index'),
    {
      part: 'tagasiside',
      vale: next.vale ? 1 : undefined,
      question: next.question ?? undefined,
      company: next.company ?? undefined,
      period: next.period === 'all' ? undefined : next.period,
      page: page > 1 ? page : undefined,
    },
    {
      only: ['feedback'],
      preserveState: true,
      preserveScroll: true,
      replace: true,
      onStart: () => (loading.value = true),
      onFinish: () => (loading.value = false),
    },
  )
}

const onPage = event => visit({}, event.page + 1)

const dateTime = iso =>
  iso ? new Intl.DateTimeFormat('et-EE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : ''

const selected = ref(null)
const drawerOpen = ref(false)
const openRow = event => {
  selected.value = event.data
  drawerOpen.value = true
}
</script>

<template>
  <div>
  <Card data-testid="feedback-list">
    <template #title>{{ t('messages.admin.report_qc.feedback.title') }}</template>
    <template #subtitle>{{ t('messages.admin.report_qc.feedback.hint') }}</template>
    <template #content>
      <div v-if="!feedback.anyFeedback" class="py-6 text-center" data-testid="feedback-empty">
        <p class="font-medium text-gray-700">{{ t('messages.admin.report_qc.feedback.empty') }}</p>
        <p class="mt-1 text-sm text-gray-500">{{ t('messages.admin.report_qc.feedback.emptyHint') }}</p>
      </div>

      <template v-else>
        <p class="mb-4 text-sm text-gray-700" data-testid="feedback-summary">{{ summaryLine }}</p>

        <div
          class="mb-4 flex flex-wrap items-center gap-3"
          role="group"
          :aria-label="t('messages.admin.report_qc.feedback.filterLabel')"
        >
          <label class="flex items-center gap-2 text-sm text-gray-700">
            <ToggleSwitch
              :model-value="filters.vale"
              data-testid="feedback-filter-vale"
              @update:model-value="value => visit({ vale: value })"
            />
            {{ t('messages.admin.report_qc.feedback.onlyVale') }}
          </label>
          <Select
            :model-value="filters.question"
            :options="questionOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('messages.admin.report_qc.feedback.allQuestions')"
            class="w-full md:w-72"
            :aria-label="t('messages.admin.report_qc.feedback.allQuestions')"
            data-testid="feedback-filter-question"
            @update:model-value="value => visit({ question: value })"
          />
          <Select
            :model-value="filters.company"
            :options="companyOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('messages.admin.report_qc.feedback.companyAll')"
            filter
            class="w-full md:w-56"
            :aria-label="t('messages.admin.report_qc.feedback.companyAll')"
            data-testid="feedback-filter-company"
            @update:model-value="value => visit({ company: value })"
          />
          <Select
            :model-value="filters.period"
            :options="periodOptions"
            option-label="label"
            option-value="value"
            class="w-full md:w-48"
            :aria-label="t('messages.admin.report_qc.feedback.period')"
            data-testid="feedback-filter-period"
            @update:model-value="value => visit({ period: value })"
          />
        </div>

        <DataTable
          :value="feedback.rows"
          lazy
          paginator
          :rows="feedback.perPage"
          :total-records="feedback.total"
          :first="(feedback.page - 1) * feedback.perPage"
          :loading="loading"
          data-key="id"
          row-hover
          class="cursor-pointer text-sm"
          data-testid="feedback-table"
          @page="onPage"
          @row-click="openRow"
        >
          <template #empty>
            <p class="py-4 text-center text-gray-500" data-testid="feedback-empty-filtered">
              {{ t('messages.admin.report_qc.feedback.emptyFiltered') }}
            </p>
          </template>
          <Column :header="t('messages.admin.report_qc.feedback.colAt')">
            <template #body="{ data }">
              <span class="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid="feedback-row">
                <span
                  v-if="data.isNew"
                  class="inline-block size-2 rounded-full bg-primary-500"
                  :aria-label="t('messages.admin.report_qc.feedback.newSince')"
                  data-testid="feedback-row-new"
                ></span>
                {{ dateTime(data.at) }}
              </span>
            </template>
          </Column>
          <Column :header="t('messages.admin.report_qc.feedback.colVerdict')">
            <template #body="{ data }">
              <span
                class="inline-flex items-center gap-1 whitespace-nowrap font-medium"
                :class="data.verdict === 'vale' ? 'text-red-600' : 'text-emerald-600'"
                :data-verdict="data.verdict"
                data-testid="feedback-verdict"
              >
                <span class="pi text-xs" :class="data.verdict === 'vale' ? 'pi-times' : 'pi-check'" aria-hidden="true"></span>
                {{ t(`messages.admin.report_qc.feedback.${data.verdict}`) }}
              </span>
            </template>
          </Column>
          <Column :header="t('messages.admin.report_qc.feedback.colFinding')">
            <template #body="{ data }">
              <p class="font-medium text-gray-800">{{ data.finding.title }}</p>
              <p class="text-xs text-gray-500">
                <span v-if="data.questionKey" class="font-mono">{{ data.questionKey }}</span>
                <template v-if="data.questionTitle"> · {{ data.questionTitle }}</template>
                <template v-if="data.checkpoints.length">
                  · {{ t('messages.admin.report_qc.feedback.checkpoint', { n: data.checkpoints.join(', ') }, data.checkpoints.length) }}
                </template>
              </p>
            </template>
          </Column>
          <Column :header="t('messages.admin.report_qc.feedback.colNote')">
            <template #body="{ data }">
              <span v-if="data.note" class="whitespace-pre-line text-gray-700">{{ data.note }}</span>
              <span v-else class="text-gray-400">{{ t('messages.admin.report_qc.feedback.noNote') }}</span>
            </template>
          </Column>
          <Column :header="t('messages.admin.report_qc.feedback.colAppraiser')">
            <template #body="{ data }">
              <p class="text-gray-800">{{ data.appraiser }}</p>
              <p v-if="data.company" class="text-xs text-gray-500">{{ data.company }}</p>
            </template>
          </Column>
          <Column :header="t('messages.admin.report_qc.feedback.colVersion')">
            <template #body="{ data }">
              <span class="font-mono text-xs text-gray-500" data-testid="feedback-version">{{ data.version ?? '—' }}</span>
            </template>
          </Column>
        </DataTable>
      </template>
    </template>
  </Card>

  <FeedbackDrawer v-model:visible="drawerOpen" :row="selected" />
  </div>
</template>
